import { useRef, useLayoutEffect, useState } from 'react';
import type { VisualComponent } from '@dtwin/shared';
import CompPreviewBox from './CompPreviewBox';

interface DashboardComponentsOverlayProps {
  components: VisualComponent[];
  pageWidth: number;
  pageHeight: number;
  backgroundColor?: string;
  backgroundImage?: string;
  zIndex?: number;
  pointerEvents?: boolean;
}

/** 2D 大屏「组件层」独立叠加组件：不含 3D、背景透明。
 *  用于在已渲染的 3D 编辑器视图上合成大屏图表（只读预览、工具栏预览、大屏选择小窗预览），
 *  避免嵌入 SceneCanvas 造成 3D 场景重复加载（大模型场景尤甚）。 */
export default function DashboardComponentsOverlay({
  components,
  pageWidth,
  pageHeight,
  backgroundColor,
  backgroundImage,
  zIndex = 10,
  pointerEvents = false,
}: DashboardComponentsOverlayProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState({ scaleX: 1, scaleY: 1 });

  // 分轴拉伸（对齐 2D 编辑/预览/发布体系）：scaleX=availW/pageWidth, scaleY=availH/pageHeight
  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const fit = () => {
      const { width, height } = el.getBoundingClientRect();
      if (width <= 0 || height <= 0) return;
      setScale({ scaleX: width / pageWidth, scaleY: height / pageHeight });
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    window.addEventListener('resize', fit);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', fit);
    };
  }, [pageWidth, pageHeight]);

  return (
    <div ref={wrapRef} style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden', background: 'transparent', zIndex }}>
      <div style={{ position: 'absolute', inset: 0, pointerEvents: pointerEvents ? 'auto' : 'none' }}>
<div
            style={{
              position: 'relative',
              width: pageWidth,
              height: pageHeight,
              transform: `scale(${scale.scaleX}, ${scale.scaleY})`,
              transformOrigin: 'top left',
              backgroundImage: backgroundImage ? `url(${backgroundImage})` : undefined,
              backgroundSize: 'cover',
            }}
          >
          {components.map((comp) => (
            <CompPreviewBox key={comp.id} comp={comp} interactive={pointerEvents} />
          ))}
        </div>
      </div>
    </div>
  );
}